import { useMutation, useQuery, useQueryClient } from 'react-query';

import { Role } from '../../../shared/enum';
import { useSecurePortalFetcher } from '../../../shared/hooks';
import {
  PaginationParams,
  PaginationResponse,
  UseReactQueryOptions,
  UseReactQueryPaginationOptions,
} from '../../../shared/models';
import { User } from '../models';

export const useUsersQuery = (
  params: PaginationParams & { status: string[]; role: string[] },
  options?: UseReactQueryPaginationOptions<
    User,
    { status: string[]; role: string[] }
  >
) => {
  const fetcher = useSecurePortalFetcher();

  return useQuery(
    ['users', params],
    async () => {
      const { pageIndex, pageSize, search, sortBy, status, role } = params;
      const { data } = await fetcher<PaginationResponse<User>>('/v1/users', {
        query: {
          page: pageIndex,
          size: pageSize,
          query: search,
          sort: sortBy,
          ...(status.length === 1 && {
            enabled: String(status[0] === 'ENABLED'),
          }),
          role,
        },
      }).json();

      return data;
    },
    options
  );
};

export const useUserQuery = (
  userId: string,
  options?: UseReactQueryOptions<User>
) => {
  const fetcher = useSecurePortalFetcher();

  return useQuery(
    ['user', userId],
    async () => {
      const { data } = await fetcher<User>(`/v1/users/${userId}`).json();

      return data;
    },
    options
  );
};

export const useEditUserMutation = () => {
  const fetcher = useSecurePortalFetcher();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      userId,
      name,
      email,
      mobileNumber,
      role,
      folderIds,
    }: {
      userId: string;
      name: string;
      email: string;
      mobileNumber: string;
      role: Role;
      folderIds: string[];
    }) => {
      const { data } = await fetcher<User>(`/v1/users/${userId}`, {
        method: 'PATCH',
        json: {
          name,
          email,
          mobileNumber,
          role,
          folderIds,
        },
      }).json();

      return data;
    },
    {
      onSuccess(data, variables) {
        queryClient.setQueryData(['user', variables.userId], data);
      },
    }
  );
};

export const useSetUserStatusMutation = () => {
  const fetcher = useSecurePortalFetcher();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ userId, enable }: { userId: string; enable: boolean }) => {
      await fetcher(`/v1/users/${userId}/${enable ? 'enable' : 'disable'}`, {
        method: 'PATCH',
      }).flush();
    },
    {
      onSuccess(data, variables) {
        const user = queryClient.getQueryData<User>(['user', variables.userId]);

        if (user) {
          queryClient.setQueryData(['user', variables.userId], {
            ...user,
            enabled: variables.enable,
          });
        }
      },
    }
  );
};

export type AddUserRequest = {
  name: string;
  email: string;
  mobileNumber: string;
  role: Role;
  folderIds: string[];
};

export const useAddUserMutation = () => {
  const fetcher = useSecurePortalFetcher();

  return useMutation(async (body: AddUserRequest) => {
    const { data } = await fetcher<User>('/v1/users', {
      method: 'POST',
      json: body,
    }).json();

    return data;
  });
};
